import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SCREENS, SCREENS_MAX } from '../responsive';
import { SectionDrawer } from './SectionDrawer';
import { SectionIcon } from './sectionIcons';
import {
  DESKTOP_SCROLL_OFFSET,
  MOBILE_BAR_HEIGHT,
  MOBILE_SCROLL_OFFSET,
  bottomSpacerHeight,
  followBox,
  isRailLayout,
  pickActiveSection,
  prefersReducedMotion,
  readExpanded,
  scrollContainerOf,
  scrollerViewHeight,
  scrollScrollerTo,
  sectionIdFromHash,
  topInScroller,
  topInView,
  writeExpanded,
} from './sectionNavRules';
import type { NavGroup, NavSection } from './types';

export type { NavGroup, NavSection } from './types';

const RAIL_WIDTH = 80;
const RAIL_WIDTH_EXPANDED = 248;

/* ---------------------------------------------------------------- layout */

const Row = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  min-height: calc(100vh - 68px);
  background-color: var(--color-bg-canvas);
`;

/** Keeps the rail's width in the flow; the rail itself is fixed over it. */
const RailSlot = styled.div<{ $expanded: boolean }>`
  display: none;
  flex: none;
  @media (min-width: ${SCREENS.md}) {
    display: block;
    width: ${RAIL_WIDTH}px;
  }
  @media (min-width: ${SCREENS.lg}) {
    width: ${({ $expanded }) => ($expanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH)}px;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

/* ------------------------------------------------------------------ rail */

const Rail = styled.nav<{ $expanded: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  height: 0;
  z-index: var(--z-sticky);
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  background-color: var(--color-bg-elevated);
  border-right: 1px solid var(--color-border);
  padding: 12px 8px 20px;
  @media (min-width: ${SCREENS.md}) {
    display: block;
    width: ${RAIL_WIDTH}px;
  }
  @media (min-width: ${SCREENS.lg}) {
    width: ${({ $expanded }) => ($expanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH)}px;
    padding: ${({ $expanded }) => ($expanded ? '12px 12px 20px' : '12px 8px 20px')};
  }
`;

const RailTop = styled.div`
  display: none;
  @media (min-width: ${SCREENS.lg}) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 8px;
  }
`;

const RailTitle = styled.span`
  margin-right: auto;
  padding-left: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

const ToggleButton = styled.button`
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-bg-elevated);
  color: var(--color-text-primary);
  cursor: pointer;
  &:hover {
    background-color: var(--color-bg-secondary);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Group = styled.div`
  & + & {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--color-border);
  }
`;

const GroupName = styled.div<{ $expanded: boolean }>`
  display: none;
  @media (min-width: ${SCREENS.lg}) {
    display: ${({ $expanded }) => ($expanded ? 'block' : 'none')};
    padding: 6px 10px 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-text-secondary);
  }
`;

const RailItem = styled.a<{ $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 2px 7px;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  .section-nav-full {
    display: none;
  }
  &:hover {
    background-color: var(--color-bg-secondary);
    color: var(--color-text-primary);
  }
  &[aria-current='location'] {
    background-color: var(--color-bg-secondary);
    color: var(--color-text-primary);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  @media (min-width: ${SCREENS.lg}) {
    ${({ $expanded }) =>
      $expanded
        ? `
      flex-direction: row;
      gap: 12px;
      padding: 9px 10px;
      font-size: 14px;
      font-weight: 500;
      text-align: left;
      .section-nav-short { display: none; }
      .section-nav-full { display: inline; }
      &[aria-current='location'] {
        font-weight: 700;
        box-shadow: inset 3px 0 0 var(--color-black);
      }
    `
        : ''}
  }
`;

const Tooltip = styled.div`
  position: fixed;
  z-index: var(--z-overlay);
  pointer-events: none;
  white-space: nowrap;
  padding: 6px 9px;
  border-radius: 6px;
  background-color: rgb(17 24 39);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  box-shadow: var(--shadow-menu);
`;

/* ------------------------------------------------------------- phone bar */

const BarSlot = styled.div`
  height: ${MOBILE_BAR_HEIGHT}px;
  flex: none;
  @media (min-width: ${SCREENS.md}) {
    display: none;
  }
`;

const Bar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: var(--z-sticky);
  height: ${MOBILE_BAR_HEIGHT}px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 max(8px, env(safe-area-inset-right, 0px)) 0 max(8px, env(safe-area-inset-left, 0px));
  background-color: var(--color-bg-elevated);
  border-bottom: 1px solid var(--color-border);
  @media (min-width: ${SCREENS.md}) {
    display: none;
  }
  @media (max-width: ${SCREENS_MAX.md}) {
    display: flex;
  }
`;

const DotsButton = styled.button`
  width: 40px;
  height: 40px;
  flex: none;
  display: grid;
  place-items: center;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-bg-elevated);
  color: var(--color-text-primary);
  cursor: pointer;
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const BarTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const BarCount = styled.span`
  margin-left: auto;
  padding-right: 4px;
  font-size: 12px;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
`;

/* --------------------------------------------------------------- section */

const SectionAnchor = styled.section`
  scroll-margin-top: ${MOBILE_SCROLL_OFFSET}px;
  @media (min-width: ${SCREENS.md}) {
    scroll-margin-top: ${DESKTOP_SCROLL_OFFSET}px;
  }
  &:focus {
    outline: none;
  }
`;

/**
 * Wraps one card so the nav can jump to it (`#id`). Focus lands here after a
 * keyboard jump, so screen readers announce the section by its label.
 */
export function SettingsSection({
  id,
  label,
  children,
}: {
  id?: string;
  label?: string;
  children: ReactNode;
}) {
  if (!id) return <>{children}</>;
  return (
    <SectionAnchor id={id} aria-label={label} tabIndex={-1} data-section-anchor>
      {children}
    </SectionAnchor>
  );
}

/* ---------------------------------------------------------------- layout */

export interface SectionNavLayoutProps {
  sections: NavSection[];
  groups: NavGroup[];
  /** Accessible name of the navigation, e.g. "Brand settings sections". */
  label: string;
  /** `localStorage` key for the expanded rail (1024px and up). */
  storageKey: string;
  /** False while the page has no cards yet (loading, error): no nav is drawn. */
  ready: boolean;
  children: ReactNode;
}

interface Lock {
  id: string;
  until: number;
}

/**
 * Spec 030: a page with a left section nav. From 768px an 80px icon rail
 * (expandable to 248px from 1024px); below 768px a bar with a ⋯ button that
 * opens a drawer. Every item scrolls to its `SettingsSection`.
 */
export function SectionNavLayout({
  sections,
  groups,
  label,
  storageKey,
  ready,
  children,
}: SectionNavLayoutProps) {
  const [expanded, setExpanded] = useState(() => readExpanded(storageKey));
  const [activeId, setActiveId] = useState<string | null>(sections[0]?.id ?? null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tip, setTip] = useState<{ text: string; top: number; left: number } | null>(null);

  const rowRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const barSlotRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLButtonElement>(null);
  const lockRef = useRef<Lock | null>(null);
  const idleTimer = useRef<number | undefined>(undefined);
  const tipTimer = useRef<number | undefined>(undefined);
  const frame = useRef<number | undefined>(undefined);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const offset = () => (isRailLayout() ? DESKTOP_SCROLL_OFFSET : MOBILE_SCROLL_OFFSET);

  const releaseLockSoon = (ms: number) => {
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      lockRef.current = null;
    }, ms);
  };

  const update = useCallback(() => {
    frame.current = undefined;
    const row = rowRef.current;
    if (!row) return;
    const vh = window.innerHeight;
    const box = row.getBoundingClientRect();
    const slotTop = barSlotRef.current?.getBoundingClientRect().top ?? box.top;
    const pos = followBox(box, vh, slotTop, MOBILE_BAR_HEIGHT);
    if (railRef.current) {
      railRef.current.style.top = `${pos.railTop}px`;
      railRef.current.style.height = `${pos.railHeight}px`;
      railRef.current.style.left = `${Math.max(0, box.left)}px`;
    }
    if (barRef.current) barRef.current.style.top = `${pos.barTop}px`;

    const anchors = sectionsRef.current
      .map(s => document.getElementById(s.id))
      .filter((el): el is HTMLElement => !!el);
    if (anchors.length === 0) return;
    const scroller = scrollContainerOf(row);
    const viewHeight = scrollerViewHeight(scroller);

    const spacer = spacerRef.current;
    if (spacer) {
      const current = spacer.offsetHeight;
      const last = anchors[anchors.length - 1];
      const next = bottomSpacerHeight({
        lastSectionTopInDoc: topInScroller(last, scroller),
        offset: offset(),
        docHeight: scroller.scrollHeight - current,
        viewportHeight: viewHeight,
      });
      if (Math.abs(next - current) > 1) spacer.style.height = `${next}px`;
    }

    const lock = lockRef.current;
    if (lock && Date.now() < lock.until) {
      setActiveId(lock.id);
      return;
    }
    lockRef.current = null;
    const atBottom =
      scroller.scrollTop > 0 && scroller.scrollTop + viewHeight >= scroller.scrollHeight - 2;
    const next = pickActiveSection(
      anchors.map(el => ({ id: el.id, top: topInView(el, scroller) })),
      offset(),
      atBottom,
    );
    if (next) setActiveId(next);
  }, []);

  const schedule = useCallback(() => {
    if (frame.current === undefined) frame.current = window.requestAnimationFrame(update);
  }, [update]);

  useLayoutEffect(() => {
    if (!ready) return undefined;
    update();
    const onScroll = () => {
      if (lockRef.current) releaseLockSoon(150);
      setTip(null);
      schedule();
    };
    // Capture: the page scrolls inside <body>, whose scroll events do not reach window.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && rowRef.current) {
      observer = new ResizeObserver(schedule);
      observer.observe(rowRef.current);
    }
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      if (frame.current !== undefined) window.cancelAnimationFrame(frame.current);
      frame.current = undefined;
    };
  }, [ready, schedule, update]);

  useEffect(() => {
    if (ready) schedule();
  }, [ready, sections, expanded, schedule]);

  const jump = useCallback((id: string, opts: { focus?: boolean; instant?: boolean } = {}) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduced = opts.instant || prefersReducedMotion();
    const scroller = scrollContainerOf(el);
    const top = Math.max(0, topInScroller(el, scroller) - offset());
    lockRef.current = { id, until: Date.now() + 2500 };
    releaseLockSoon(reduced ? 50 : 400);
    setActiveId(id);
    try {
      window.history.replaceState(window.history.state, '', `#${id}`);
    } catch {
      // Some embedded views refuse history writes; the jump still works.
    }
    scrollScrollerTo(scroller, top, reduced ? 'auto' : 'smooth');
    if (opts.focus) el.focus({ preventScroll: true });
  }, []);

  // A link to `#section` opens on that section once the cards exist (US5).
  const jumpedFromHash = useRef(false);
  useEffect(() => {
    if (!ready || jumpedFromHash.current) return undefined;
    jumpedFromHash.current = true;
    const id = sectionIdFromHash(window.location.hash, sectionsRef.current);
    if (!id) return undefined;
    // Cards above it still load their own data and grow, so line it up again
    // while the page settles, unless the reader starts scrolling first.
    let userMoved = false;
    const stop = () => {
      userMoved = true;
    };
    const events = ['wheel', 'touchmove', 'keydown', 'mousedown'] as const;
    events.forEach(e => window.addEventListener(e, stop, { passive: true, once: true }));
    const align = () => {
      if (!userMoved) jump(id, { instant: true });
    };
    window.requestAnimationFrame(align);
    const timers = [300, 800, 1600].map(ms => window.setTimeout(align, ms));
    return () => {
      timers.forEach(t => window.clearTimeout(t));
      events.forEach(e => window.removeEventListener(e, stop));
    };
  }, [ready, jump]);

  const toggleExpanded = () => {
    setExpanded(prev => {
      writeExpanded(storageKey, !prev);
      return !prev;
    });
    setTip(null);
  };

  const showTip = (el: HTMLElement, text: string, delay: number) => {
    window.clearTimeout(tipTimer.current);
    const full = el.querySelector('.section-nav-full');
    if (full && window.getComputedStyle(full).display !== 'none') return;
    tipTimer.current = window.setTimeout(() => {
      const r = el.getBoundingClientRect();
      setTip({ text, top: r.top + r.height / 2 - 14, left: r.right + 8 });
    }, delay);
  };
  const hideTip = () => {
    window.clearTimeout(tipTimer.current);
    setTip(null);
  };

  useEffect(
    () => () => {
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(tipTimer.current);
    },
    [],
  );

  if (!ready) return <>{children}</>;

  const activeIndex = Math.max(
    0,
    sections.findIndex(s => s.id === activeId),
  );
  const active = sections[activeIndex];

  return (
    <Row ref={rowRef} data-testid="section-nav-layout">
      <RailSlot $expanded={expanded} aria-hidden="true" />
      <Rail
        ref={railRef}
        $expanded={expanded}
        aria-label={label}
        data-testid="section-nav-rail"
        onScroll={hideTip}
      >
        <RailTop>
          {expanded && <RailTitle>Sections</RailTitle>}
          <ToggleButton
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse sections' : 'Expand sections'}
            onClick={toggleExpanded}
            data-testid="section-nav-toggle"
          >
            <SectionIcon name={expanded ? 'chevronLeft' : 'chevronRight'} size={18} />
          </ToggleButton>
        </RailTop>
        {groups.map(group => {
          const items = sections.filter(s => s.group === group.id);
          if (items.length === 0) return null;
          return (
            <Group key={group.id} role="group" aria-label={group.label}>
              <GroupName $expanded={expanded} aria-hidden="true">
                {group.label}
              </GroupName>
              {items.map(s => (
                <RailItem
                  key={s.id}
                  href={`#${s.id}`}
                  $expanded={expanded}
                  aria-current={activeId === s.id ? 'location' : undefined}
                  aria-label={s.label}
                  data-testid={`section-nav-item-${s.id}`}
                  onClick={e => {
                    e.preventDefault();
                    hideTip();
                    jump(s.id, { focus: e.detail === 0 });
                  }}
                  onMouseEnter={e => showTip(e.currentTarget, s.label, 300)}
                  onMouseLeave={hideTip}
                  onFocus={e => showTip(e.currentTarget, s.label, 0)}
                  onBlur={hideTip}
                >
                  <SectionIcon name={s.icon} />
                  <span className="section-nav-short" aria-hidden="true">
                    {s.short}
                  </span>
                  <span className="section-nav-full" aria-hidden="true">
                    {s.label}
                  </span>
                </RailItem>
              ))}
            </Group>
          );
        })}
      </Rail>
      {tip && (
        <Tooltip role="tooltip" style={{ top: tip.top, left: tip.left }}>
          {tip.text}
        </Tooltip>
      )}

      <Content>
        <BarSlot ref={barSlotRef} aria-hidden="true" />
        <Bar ref={barRef} data-testid="section-nav-bar">
          <DotsButton
            ref={dotsRef}
            type="button"
            aria-label="Sections"
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
            data-testid="section-nav-dots"
          >
            <SectionIcon name="dots" />
          </DotsButton>
          <BarTitle data-testid="section-nav-current">{active?.label}</BarTitle>
          <BarCount>
            {activeIndex + 1} / {sections.length}
          </BarCount>
        </Bar>
        {children}
        <div ref={spacerRef} aria-hidden="true" data-testid="section-nav-spacer" />
      </Content>

      <SectionDrawer
        open={drawerOpen}
        title="Sections"
        sections={sections}
        groups={groups}
        activeId={activeId}
        onClose={() => {
          setDrawerOpen(false);
          dotsRef.current?.focus({ preventScroll: true });
        }}
        onSelect={(id, viaKeyboard) => {
          setDrawerOpen(false);
          // The drawer unlocks page scrolling when it closes; jump after that.
          window.requestAnimationFrame(() => jump(id, { focus: viaKeyboard }));
        }}
      />
    </Row>
  );
}
